import { FunctionComponent, SVGProps, useEffect, useState } from "react";
import { useGetContactInfo } from "api/initial/useGetContactInfo";
import { ReactComponent as UserIcon } from "assets/user-circle.svg";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { fallbackLanguage } from "../i18n";

const LANGUAGE_STORAGE_KEY = "language";
const LATVIAN_LANGUAGE = "lv-LV";
const LITHUANIAN_LANGUAGE = "lt-LT";
const COMPANY_LT_TAG = "Company-LT";

const getAvailableLanguages = (): string[] => {
    const hostname = window.location.hostname;
    const domain = hostname.split('.').pop()?.toUpperCase() ?? null;
    const languages = (process.env['REACT_APP_LANGUAGES_' + domain] ?? fallbackLanguage).split(',');

    return languages;
}

const getLanguagesForUser = (isLTUser: boolean): string[] =>
    getAvailableLanguages().filter((language) =>
        isLTUser ? language !== LATVIAN_LANGUAGE : language !== LITHUANIAN_LANGUAGE
    );

const getDefaultLanguage = (availableLanguages: string[], isLTUser: boolean): string => {
    if (isLTUser && availableLanguages.includes(LITHUANIAN_LANGUAGE)) {
        return LITHUANIAN_LANGUAGE;
    }

    if (!isLTUser && availableLanguages.includes(LATVIAN_LANGUAGE)) {
        return LATVIAN_LANGUAGE;
    }

    return availableLanguages[0] ?? fallbackLanguage;
}

export const useLanguageSwitch = () => {
    const [isReady, setIsReady] = useState(false);
    const [locale, setLocale] = useState<string|undefined>(undefined);
    const { i18n } = useModifiedTranslation();
    const { data: contactData, loading: contactLoading, error: contactError } = useGetContactInfo();

    const isLTUser = contactData?.tags?.includes(COMPANY_LT_TAG) ?? false;

    useEffect(() => {
        if (contactLoading || (!contactData && !contactError)) {
            return;
        }

        const availableLanguages = getLanguagesForUser(isLTUser);
        const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);

        if (storedLanguage && availableLanguages.includes(storedLanguage)) {
            setLocale(storedLanguage);
            return;
        }

        const defaultLanguage = getDefaultLanguage(availableLanguages, isLTUser);
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, defaultLanguage);
        setLocale(defaultLanguage);
    }, [contactLoading, contactData, contactError, isLTUser]);

    useEffect(() => {
        if (locale) {
            i18n.changeLanguage(locale, () => {
                setIsReady(true);
            });
        }

        return () => {
            setIsReady(false);
        };
    }, [i18n, locale]);

    const setLanguage = (language: string) => {
        // Preserve selected contact across language change reload
        // Use both sessionStorage (more reliable) and localStorage as fallback
        const searchParams = new URLSearchParams(window.location.search);
        const contactId = searchParams.get('contactId');
        if (contactId) {
            try {
                window.sessionStorage.setItem('selectedContactIdBeforeReload', contactId);
            } catch {
                // sessionStorage might be blocked, try localStorage
            }
            try {
                window.localStorage.setItem('selectedContactIdBeforeReload', contactId);
            } catch {
                // localStorage might be blocked
            }
        }

        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
        setLocale(language);
        window.location.reload();
    }

    const languageSwitchMenus = (): Array<string | { label: string; action: () => void; Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string | undefined; }>; selected: boolean; id: string; }> =>
    {
        const availableLanguages = getLanguagesForUser(isLTUser);

        const menu: (string | { label: string; action: () => void; Icon: FunctionComponent<SVGProps<SVGSVGElement> & { title?: string | undefined; }>; selected: boolean; id: string; })[] = [];

        if (availableLanguages.length > 1) {
            availableLanguages.forEach((lang) => {
                const splitName = lang.split('-');
                menu.push({
                    label: splitName[0].toUpperCase(),
                    action: () => {
                        setLanguage(lang);
                    },
                    Icon: UserIcon,
                    selected: locale === lang,
                    id: `userMenu-language-${lang}`,
                });
            });
        }

        return menu;
    }

    return { isReady, setLanguage, languageSwitchMenus };
};
